import React, { useEffect, useRef } from 'react';
import type { QueryKey } from '@tanstack/react-query';

import { useSSEStream } from '@/common/hooks/useSSEStream';
import { useDialog } from '@/common/context/DialogContext';
import log from '@/common/utils/logger';

/** In-flight module autograde job tracked by EvaluationDesk. */
export type AutogradeJob = {
  key: string;
  userId: string;
  courseId: string;
  moduleId: string;
  jobId?: string;
  progress: number;
  tasksQueryKey: QueryKey;
  moduleEvaluationQueryKey: QueryKey;
};

/** Stable key for a submission's module autograde job. */
export const buildAutogradeJobKey = (
  userId: string,
  courseId: string,
  moduleId: string,
): string => `${userId}|${courseId}|${moduleId}`;

type AutogradeJobWatcherProps = {
  job: AutogradeJob;
  onProgress: (key: string, progress: number) => void;
  onComplete: (key: string) => void | Promise<void>;
  onError: (key: string, error: unknown) => void;
};

/**
 * AutogradeJobWatcher
 * ----
 * Keeps one SSE connection alive for a module autograde job until it completes
 * or errors. Mounted at the EvaluationDesk screen level so navigation between
 * submissions does not tear down the stream.
 */
const AutogradeJobWatcher: React.FC<AutogradeJobWatcherProps> = ({
  job,
  onProgress,
  onComplete,
  onError,
}) => {
  const { showToastMessage } = useDialog();
  const startedJobIdRef = useRef<string | undefined>(undefined);

  const { startStream, stopStream } = useSSEStream({
    source: '',
    onMessage: (message: { progress?: number; status?: string; error?: string }) => {
      if (typeof message?.progress === 'number') {
        onProgress(job.key, message.progress);
      }
      if (message?.status === 'error') {
        showToastMessage('error', message.error || 'AutoGrading failed', 'AutoGrading');
        onError(job.key, message.error);
        stopStream();
      }
    },
    onServerClose: () => {
      onProgress(job.key, 100);
      Promise.resolve(onComplete(job.key)).catch((error) => {
        log.info('Error completing AutoGrading: ', error);
      });
    },
    onError: (error) => {
      showToastMessage('error', 'AutoGrading of module submission failed', 'AutoGrading');
      log.info('Error in AutoGrading submission: ', error);
      onError(job.key, error);
    },
  });

  useEffect(() => {
    if (!job.jobId || startedJobIdRef.current === job.jobId) {
      return;
    }
    startedJobIdRef.current = job.jobId;
    startStream(`/jobs/${job.jobId}/stream`);
  }, [job.jobId, startStream]);

  return null;
};

export default AutogradeJobWatcher;
